import React, { lazy, Suspense, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import UltronicThemeProvider from './theme/UltronicThemeProvider';
import Layout from './components/Layout';
import Home from './pages/Home';
import Calculator from './pages/Calculator';
import Results from './pages/Results';
import FinancialPlanningHub from './pages/FinancialPlanningHub';
import ChatbotWidget from './components/ChatbotWidget';
import ErrorBoundary from './components/common/ErrorBoundary';
import { PaywallModal } from './components/Paywall';
import { track } from './services/analytics';

const OverviewSection = lazy(() => import('./components/planning/OverviewSection'));
const FinancialProfileSection = lazy(() => import('./components/planning/FinancialProfileSection'));
const AccumulateSection = lazy(() => import('./components/planning/AccumulateSection'));
const SpendDownSection = lazy(() => import('./components/planning/SpendDownSection'));
const AdvancedRetirementSection = lazy(() => import('./components/planning/AdvancedRetirementSection'));
const ReportsSection = lazy(() => import('./components/planning/ReportsSection'));
const CompareSection = lazy(() => import('./components/planning/CompareSection'));
const TermsOfService = lazy(() => import('./pages/Legal').then((m) => ({ default: m.TermsOfService })));
const PrivacyPolicy = lazy(() => import('./pages/Legal').then((m) => ({ default: m.PrivacyPolicy })));
const Plans = lazy(() => import('./pages/Plans'));
const Methodology = lazy(() => import('./pages/Methodology'));
const Admin = lazy(() => import('./pages/Admin'));
const Support = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Support })));

const PageLoader = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terminal-green"></div>
  </div>
);

// One usage event per screen opened. The route only: nothing from the plan.
function PageViews() {
  const { pathname } = useLocation();
  useEffect(() => {
    track('page_view');
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AppProvider>
      <UltronicThemeProvider>
        <Router>
          <PageViews />
          <Layout>
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/real-estate" element={<Calculator />} />
                  <Route path="/results" element={<Results />} />

                  {/* Financial Planning Hub */}
                  <Route path="/financial-planning" element={<FinancialPlanningHub />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<OverviewSection />} />
                    <Route path="profile" element={<FinancialProfileSection />} />
                    <Route path="accumulate" element={<AccumulateSection />} />
                    <Route path="spend-down" element={<SpendDownSection />} />
                    <Route path="advanced" element={<AdvancedRetirementSection />} />
                    <Route path="compare" element={<CompareSection />} />
                    <Route path="reports" element={<ReportsSection />} />
                  </Route>

                  {/* Redirects from old routes */}
                  <Route path="/finance" element={<Navigate to="/financial-planning/accumulate" replace />} />
                  <Route path="/accumulate" element={<Navigate to="/financial-planning/accumulate" replace />} />
                  <Route path="/spend-down" element={<Navigate to="/financial-planning/spend-down" replace />} />

                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/methodology" element={<Methodology />} />
                  <Route path="/plans" element={<Plans />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/pricing" element={<Navigate to="/plans" replace />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>

            <ChatbotWidget />
            <PaywallModal />
          </Layout>
        </Router>
      </UltronicThemeProvider>
    </AppProvider>
  );
}

export default App;
